import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { QRCodeSVG } from 'qrcode.react';

import {
  createLineLinkSessionApi,
  getLineLinkSessionStatusApi,
  getLineStatusApi,
  setLinePromptPreferenceApi,
  startLineVerificationApi,
} from '../../util/api';
import { useIntl } from '../../util/reactIntl';
import { getMobileOperatingSystem, isCommonInAppBrowser } from '../../util/userAgent';
import IconClose from '../IconClose/IconClose';
import IconSpinner from '../IconSpinner/IconSpinner';
import Modal from '../Modal/Modal';
import modalCss from '../Modal/Modal.module.css';

import css from './LineConnectModal.module.css';

const POLL_INTERVAL_MS = 4000;
const PENDING_TIMEOUT_MS = 120000;
const OAUTH_LAUNCH_TIMEOUT_MS = 15000;
export const DEFAULT_LINE_OFFICIAL_ACCOUNT_URL = 'https://line.me/R/ti/p/@sceneaccess';

export const getLineOAuthLaunchEnvironment = () => {
  const os = getMobileOperatingSystem();
  return {
    os,
    isMobile: os === 'ios' || os === 'android',
    isInAppBrowser: isCommonInAppBrowser(),
  };
};

const getOpenLineUrl = session =>
  session?.lineDeepLink ||
  session?.lineOfficialAccountUrl ||
  session?.officialAccountDeepLink ||
  session?.officialAccountUrl ||
  session?.officialAccountId ||
  DEFAULT_LINE_OFFICIAL_ACCOUNT_URL;

const getQrValue = session =>
  session?.qrValue ||
  session?.lineOfficialAccountUrl ||
  session?.officialAccountUrl ||
  session?.lineDeepLink ||
  session?.officialAccountDeepLink ||
  DEFAULT_LINE_OFFICIAL_ACCOUNT_URL;

export const redirectToLineOAuth = redirectUrl => {
  if (!redirectUrl || typeof window === 'undefined') {
    return false;
  }

  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = redirectUrl;
      return true;
    }
  } catch (e) {
    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    return true;
  }

  window.location.assign(redirectUrl);
  return true;
};

export const getAbsoluteLineReturnTo = returnTo => {
  if (typeof window === 'undefined') {
    return returnTo || '/';
  }

  const target = String(
    returnTo ||
      `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash ||
        ''}`
  );
  if (/^https?:\/\//i.test(target)) {
    return target;
  }

  let origin = window.location.origin;
  try {
    origin =
      window.top && window.top !== window.self
        ? window.top.location.origin
        : window.location.origin;
  } catch (e) {
    origin = window.location.origin;
  }

  return `${origin}${target.startsWith('/') ? target : `/${target}`}`;
};

const getExternallyProvidedSession = props => {
  if (props.initialSession) {
    return props.initialSession;
  }

  const hasExternalSession =
    props.lineOfficialAccountUrl ||
    props.lineQrUrl ||
    props.lineDeepLink ||
    props.sessionId ||
    props.officialAccountId ||
    props.lineOfficialAccountId;

  return hasExternalSession
    ? {
        lineOfficialAccountUrl: props.lineOfficialAccountUrl,
        lineQrUrl: props.lineQrUrl,
        lineDeepLink: props.lineDeepLink,
        sessionId: props.sessionId,
        officialAccountId: props.officialAccountId,
        lineOfficialAccountId: props.lineOfficialAccountId,
      }
    : null;
};

const LineIcon = () => (
  <span className={css.lineIcon} aria-hidden="true">
    LINE
  </span>
);

const LineQrCode = props => {
  const { qrValue, lineQrUrl, ariaLabel } = props;
  if (qrValue) {
    return (
      <QRCodeSVG
        className={css.qrSvg}
        value={qrValue}
        size={160}
        marginSize={2}
        role="img"
        aria-label={ariaLabel}
      />
    );
  }

  if (lineQrUrl) {
    return <img className={css.qrSvg} src={lineQrUrl} alt={ariaLabel} />;
  }

  return null;
};

const StepHeading = props => {
  const { number, children } = props;
  return (
    <div className={css.stepHeading}>
      <span className={css.stepBadge}>{number}</span>
      <h3>{children}</h3>
    </div>
  );
};

const StatePanel = props => {
  const {
    state,
    titleId,
    descriptionId,
    onDone,
    onRetry,
    isRetrying,
    errorDescription,
    messages,
  } = props;
  const isPending = state === 'pending';
  const isConnected = state === 'connected';
  const isExpired = state === 'expired';
  const title = isPending
    ? messages.connectingTitle
    : isConnected
    ? messages.connectedTitle
    : isExpired
    ? messages.expiredTitle
    : messages.failedTitle;
  const description = isPending
    ? messages.pendingDescription
    : isConnected
    ? messages.connectedDescription
    : isExpired
    ? messages.expiredDescription
    : errorDescription || messages.failedDescription;

  return (
    <div className={css.statePanel}>
      {isPending ? (
        <IconSpinner rootClassName={css.stateSpinner} />
      ) : isConnected ? (
        <span className={css.connectedMark} aria-hidden="true">
          ✓
        </span>
      ) : (
        <span className={css.errorMark} aria-hidden="true">
          !
        </span>
      )}
      <h2 id={titleId} className={css.stateTitle}>
        {title}
      </h2>
      <p id={descriptionId} className={css.stateDescription}>
        {description}
      </p>
      {!isPending ? (
        <button
          type="button"
          className={isConnected ? css.lineButton : css.retryButton}
          onClick={isConnected ? onDone : onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? messages.retrying : isConnected ? messages.done : messages.tryAgain}
        </button>
      ) : null}
    </div>
  );
};

const LineConnectModal = props => {
  const {
    id = 'LineConnectModal',
    open,
    isOpen: legacyIsOpen,
    onOpenChange,
    onClose,
    onManageDisableScrolling,
    onStatusChanged,
    onSkip,
    onDontAskAgain,
    onStartVerify,
    onConnected,
    onSessionReady,
    onLineActionStarted,
    returnTo,
    source,
    role,
    context = 'prompt',
    isVerifying: externalVerifying = false,
  } = props;

  const intl = useIntl();
  const isOpen = open != null ? open : legacyIsOpen;
  const modalRole = role || (context === 'settings' ? 'host' : context);
  const externalSession = useMemo(() => getExternallyProvidedSession(props), [
    props.initialSession,
    props.lineOfficialAccountUrl,
    props.lineQrUrl,
    props.lineDeepLink,
    props.sessionId,
    props.officialAccountId,
    props.lineOfficialAccountId,
  ]);
  const [session, setSession] = useState(externalSession);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [phase, setPhase] = useState('prompt');
  const [errorDescription, setErrorDescription] = useState('');
  const [doNotAskAgain, setDoNotAskAgain] = useState(false);
  const [showMobileQr, setShowMobileQr] = useState(false);
  const [sessionRequestKey, setSessionRequestKey] = useState(0);
  const [verificationLaunch, setVerificationLaunch] = useState(null);
  const [verificationLaunchError, setVerificationLaunchError] = useState(null);
  const [verificationLaunchLoading, setVerificationLaunchLoading] = useState(false);
  const launchStartedRef = useRef(false);
  const launchFailureTimerRef = useRef(null);
  const messages = useMemo(
    () => ({
      title: intl.formatMessage({ id: 'LineConnectModal.title' }),
      subtitle: intl.formatMessage({ id: 'LineConnectModal.subtitle' }),
      stepAddOfficialAccount: intl.formatMessage({
        id: 'LineConnectModal.stepAddOfficialAccount',
      }),
      openLine: intl.formatMessage({ id: 'LineConnectModal.openLine' }),
      hideQr: intl.formatMessage({ id: 'LineConnectModal.hideQr' }),
      showQrInstead: intl.formatMessage({ id: 'LineConnectModal.showQrInstead' }),
      qrAriaLabel: intl.formatMessage({ id: 'LineConnectModal.qrAriaLabel' }),
      qrHelp: intl.formatMessage({ id: 'LineConnectModal.qrHelp' }),
      openOnDevice: intl.formatMessage({ id: 'LineConnectModal.openOnDevice' }),
      stepVerify: intl.formatMessage({ id: 'LineConnectModal.stepVerify' }),
      stepVerifyDescription: intl.formatMessage({
        id: 'LineConnectModal.stepVerifyDescription',
      }),
      continueWithLine: intl.formatMessage({ id: 'LineConnectModal.continueWithLine' }),
      opening: intl.formatMessage({ id: 'LineConnectModal.opening' }),
      openLineOAuthError: intl.formatMessage({ id: 'LineConnectModal.openLineOAuthError' }),
      preparingLine: intl.formatMessage({ id: 'LineConnectModal.preparingLine' }),
      dontShowAgain: intl.formatMessage({ id: 'LineConnectModal.dontShowAgain' }),
      maybeLater: intl.formatMessage({ id: 'LineConnectModal.maybeLater' }),
      close: intl.formatMessage({ id: 'LineConnectModal.close' }),
      connectingTitle: intl.formatMessage({ id: 'LineConnectModal.connectingTitle' }),
      connectedTitle: intl.formatMessage({ id: 'LineConnectModal.connectedTitle' }),
      expiredTitle: intl.formatMessage({ id: 'LineConnectModal.expiredTitle' }),
      failedTitle: intl.formatMessage({ id: 'LineConnectModal.failedTitle' }),
      pendingDescription: intl.formatMessage({ id: 'LineConnectModal.pendingDescription' }),
      connectedDescription: intl.formatMessage({
        id: 'LineConnectModal.connectedDescription',
      }),
      expiredDescription: intl.formatMessage({ id: 'LineConnectModal.expiredDescription' }),
      failedDescription: intl.formatMessage({ id: 'LineConnectModal.failedDescription' }),
      retrying: intl.formatMessage({ id: 'LineConnectModal.retrying' }),
      done: intl.formatMessage({ id: 'LineConnectModal.done' }),
      tryAgain: intl.formatMessage({ id: 'LineConnectModal.tryAgain' }),
      startVerificationError: intl.formatMessage({
        id: 'LineConnectModal.startVerificationError',
      }),
      verifyConnectionError: intl.formatMessage({
        id: 'LineConnectModal.verifyConnectionError',
      }),
      pendingTimeoutError: intl.formatMessage({ id: 'LineConnectModal.pendingTimeoutError' }),
    }),
    [intl]
  );

  const clearLaunchFailureTimer = useCallback(() => {
    if (launchFailureTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(launchFailureTimerRef.current);
      launchFailureTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (externalSession) {
      setSession(externalSession);
    }
  }, [externalSession]);

  useEffect(() => {
    let cancelled = false;
    if (!isOpen) {
      return () => {};
    }

    setPhase('prompt');
    setErrorDescription('');
    setDoNotAskAgain(false);
    setShowMobileQr(false);
    setVerificationLaunch(null);
    setVerificationLaunchError(null);
    setVerificationLaunchLoading(false);
    clearLaunchFailureTimer();
    launchStartedRef.current = false;

    if (externalSession) {
      setSession(externalSession);
      return () => {};
    }

    setIsCreatingSession(true);
    setSession(null);
    createLineLinkSessionApi({ returnTo, source, role: modalRole })
      .then(data => {
        if (!cancelled) {
          setSession(data);
          onSessionReady?.(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErrorDescription(messages.startVerificationError);
          setPhase('error');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsCreatingSession(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    clearLaunchFailureTimer,
    externalSession,
    isOpen,
    modalRole,
    onSessionReady,
    returnTo,
    sessionRequestKey,
    source,
  ]);

  useEffect(() => {
    if (!isOpen || phase !== 'pending') {
      return () => {};
    }

    const pollStatus = () => {
      const sessionId = session?.sessionId;
      const statusPromise = sessionId ? getLineLinkSessionStatusApi(sessionId) : getLineStatusApi();

      statusPromise
        .then(status => {
          if (status?.status === 'connected' || status?.connected) {
            setPhase('connected');
            onStatusChanged?.(status);
            onConnected?.(status);
          } else if (status?.status === 'expired') {
            setPhase('expired');
          } else if (status?.status === 'error') {
            setErrorDescription(messages.verifyConnectionError);
            setPhase('error');
          }
        })
        .catch(() => null);
    };

    pollStatus();
    const timer = window.setInterval(pollStatus, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isOpen, phase, session?.sessionId, onConnected, onStatusChanged]);

  useEffect(() => {
    if (!isOpen || phase !== 'pending') {
      return () => {};
    }

    const timeout = window.setTimeout(() => {
      setErrorDescription(messages.pendingTimeoutError);
      setPhase('error');
    }, PENDING_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [isOpen, phase]);

  const openLineUrl = getOpenLineUrl(session);
  const officialAccountUrl = session?.lineOfficialAccountUrl || session?.officialAccountUrl || '';
  const deviceLinkUrl = openLineUrl || officialAccountUrl;
  const qrValue = getQrValue(session);
  const lineQrUrl = session?.lineQrUrl || session?.officialAccountQrUrl || '';
  const loadingSession = isCreatingSession;
  const verifying = externalVerifying || isVerifying;
  const prefetchedOAuthUrl = verificationLaunch?.redirectUrl || '';
  const launchEnvironment = verificationLaunch?.environment || getLineOAuthLaunchEnvironment();
  const shouldUsePrefetchedOAuthLink = !!prefetchedOAuthUrl && !onStartVerify;
  const shouldPreparePrefetchedOAuthLink =
    !onStartVerify && isOpen && phase === 'prompt' && !!session;
  const preparingOAuthLaunch =
    shouldPreparePrefetchedOAuthLink &&
    !prefetchedOAuthUrl &&
    !verificationLaunchError &&
    (verificationLaunchLoading || !loadingSession);
  const descriptionId = `${id}-description`;
  const titleId = `${id}-title`;
  const checkboxId = `${id}-dontAskAgain`;

  const getVerificationPayload = useCallback(
    () => ({
      returnTo: getAbsoluteLineReturnTo(returnTo),
      source,
      role: modalRole,
      sessionId: session?.sessionId,
    }),
    [modalRole, returnTo, session?.sessionId, source]
  );

  useEffect(() => {
    let cancelled = false;
    launchStartedRef.current = false;
    setVerificationLaunch(null);
    setVerificationLaunchError(null);
    setVerificationLaunchLoading(false);

    if (!isOpen || phase !== 'prompt' || onStartVerify || loadingSession || !session) {
      return () => {
        cancelled = true;
      };
    }

    const verificationPayload = getVerificationPayload();
    setVerificationLaunchLoading(true);
    Promise.resolve(startLineVerificationApi(verificationPayload))
      .then(result => {
        if (cancelled) {
          return;
        }
        const redirectUrl = result?.redirectUrl || result?.url;
        if (redirectUrl) {
          setVerificationLaunch({
            redirectUrl,
            result,
            payload: verificationPayload,
            environment: getLineOAuthLaunchEnvironment(),
          });
        } else {
          setVerificationLaunchError(true);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setVerificationLaunchError(error || true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setVerificationLaunchLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, getVerificationPayload, loadingSession, onStartVerify, phase, sessionRequestKey]);

  const setOpen = nextOpen => {
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      clearLaunchFailureTimer();
      onClose?.();
    }
  };

  const handleDismiss = () => {
    const preferencePromise = doNotAskAgain
      ? Promise.resolve(onDontAskAgain ? onDontAskAgain() : setLinePromptPreferenceApi(true))
      : Promise.resolve(onSkip?.());

    preferencePromise
      .then(status => {
        if (doNotAskAgain && status) {
          onStatusChanged?.(status);
        }
      })
      .finally(() => setOpen(false));
  };

  const handleDone = () => {
    setOpen(false);
  };

  const handleStartVerify = () => {
    if (verifying || launchStartedRef.current) {
      return;
    }

    launchStartedRef.current = true;
    setErrorDescription('');

    setIsVerifying(true);
    const verificationPayload = getVerificationPayload();
    const verificationPromise = onStartVerify
      ? onStartVerify(verificationPayload)
      : startLineVerificationApi(verificationPayload);

    Promise.resolve(verificationPromise)
      .then(result => {
        const redirectUrl = result?.redirectUrl || result?.url;
        if (redirectUrl) {
          onLineActionStarted?.(session);
          redirectToLineOAuth(redirectUrl);
          return;
        }
        setPhase(result?.connected ? 'connected' : 'pending');
      })
      .catch(() => {
        launchStartedRef.current = false;
        setErrorDescription(messages.startVerificationError);
        setPhase('error');
      })
      .finally(() => setIsVerifying(false));
  };

  const handleOAuthLinkClick = event => {
    if (verifying || launchStartedRef.current) {
      event.preventDefault();
      return;
    }

    if (!prefetchedOAuthUrl) {
      event.preventDefault();
      if (verificationLaunchError) {
        handleStartVerify();
      }
      return;
    }

    launchStartedRef.current = true;
    setErrorDescription('');
    setIsVerifying(true);
    onLineActionStarted?.(session);

    clearLaunchFailureTimer();
    if (typeof window !== 'undefined') {
      launchFailureTimerRef.current = window.setTimeout(() => {
        if (launchStartedRef.current) {
          launchStartedRef.current = false;
          setIsVerifying(false);
          setErrorDescription(messages.openLineOAuthError);
          setPhase('error');
        }
      }, OAUTH_LAUNCH_TIMEOUT_MS);
    }
  };

  const handleRetry = () => {
    clearLaunchFailureTimer();
    launchStartedRef.current = false;
    setIsVerifying(false);
    setPhase('prompt');
    setErrorDescription('');
    setSessionRequestKey(current => current + 1);
  };

  const handleLineEntryClick = () => {
    onLineActionStarted?.(session);
  };

  const qrCardClasses = classNames(css.qrCard, {
    [css.qrCardCollapsed]: !showMobileQr,
  });

  const promptContent = (
    <>
      <div className={css.header}>
        <LineIcon />
        <div>
          <h2 id={titleId} className={css.title}>
            {messages.title}
          </h2>
          <p id={descriptionId} className={css.subtitle}>
            {messages.subtitle}
          </p>
        </div>
      </div>

      <section className={css.stepSection} aria-labelledby={`${id}-step1`}>
        <StepHeading number="1">
          <span id={`${id}-step1`}>{messages.stepAddOfficialAccount}</span>
        </StepHeading>

        <div className={css.mobileOpenActions}>
          <a
            className={css.mobileOpenLineButton}
            href={openLineUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLineEntryClick}
          >
            {messages.openLine}
          </a>
          <button
            type="button"
            className={css.showQrButton}
            onClick={() => setShowMobileQr(current => !current)}
          >
            {showMobileQr ? messages.hideQr : messages.showQrInstead}
          </button>
        </div>

        <div className={qrCardClasses}>
          <div className={css.qrFrame}>
            <LineQrCode qrValue={qrValue} lineQrUrl={lineQrUrl} ariaLabel={messages.qrAriaLabel} />
          </div>
          <p className={css.qrHelp}>{messages.qrHelp}</p>
          {deviceLinkUrl ? (
            <a
              className={css.deviceLink}
              href={deviceLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLineEntryClick}
            >
              {messages.openOnDevice}
            </a>
          ) : null}
        </div>
      </section>

      <section className={css.stepSection} aria-labelledby={`${id}-step2`}>
        <StepHeading number="2">
          <span id={`${id}-step2`}>{messages.stepVerify}</span>
        </StepHeading>
        <p className={css.stepDescription}>{messages.stepVerifyDescription}</p>
        {shouldUsePrefetchedOAuthLink ? (
          <a
            className={css.lineButton}
            href={prefetchedOAuthUrl}
            onClick={handleOAuthLinkClick}
            data-line-launch-os={launchEnvironment.os}
            data-line-in-app-browser={launchEnvironment.isInAppBrowser ? 'true' : 'false'}
            aria-disabled={verifying ? 'true' : undefined}
          >
            {verifying ? (
              <>
                <IconSpinner rootClassName={css.buttonSpinner} />
                {messages.opening}
              </>
            ) : (
              messages.continueWithLine
            )}
          </a>
        ) : (
          <button
            type="button"
            className={css.lineButton}
            onClick={handleStartVerify}
            disabled={verifying || loadingSession || preparingOAuthLaunch}
          >
            {verifying || preparingOAuthLaunch ? (
              <>
                <IconSpinner rootClassName={css.buttonSpinner} />
                {preparingOAuthLaunch ? messages.preparingLine : messages.opening}
              </>
            ) : (
              messages.continueWithLine
            )}
          </button>
        )}
      </section>

      <div className={css.footer}>
        <label className={css.checkboxRow} htmlFor={checkboxId}>
          <input
            id={checkboxId}
            className={css.checkbox}
            type="checkbox"
            checked={doNotAskAgain}
            onChange={event => setDoNotAskAgain(event.target.checked)}
          />
          <span className={css.checkboxControl} aria-hidden="true" />
          <span>{messages.dontShowAgain}</span>
        </label>
        <button type="button" className={css.secondaryButton} onClick={handleDismiss}>
          {messages.maybeLater}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      id={id}
      isOpen={!!isOpen}
      onClose={handleDismiss}
      onManageDisableScrolling={onManageDisableScrolling}
      scrollLayerClassName={classNames(modalCss.scrollLayer, css.modalScrollLayer)}
      containerClassName={classNames(modalCss.container, css.modalContainer)}
      contentClassName={classNames(modalCss.content, css.modalContent)}
      hideCloseButton
      usePortal
    >
      <div
        className={css.root}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-line-role={modalRole}
      >
        <button
          type="button"
          className={css.closeButton}
          onClick={handleDismiss}
          aria-label={messages.close}
        >
          <IconClose rootClassName={css.closeIcon} />
        </button>

        {phase === 'prompt' ? (
          promptContent
        ) : (
          <StatePanel
            state={phase}
            titleId={titleId}
            descriptionId={descriptionId}
            onDone={handleDone}
            onRetry={handleRetry}
            isRetrying={loadingSession}
            errorDescription={errorDescription}
            messages={messages}
          />
        )}
      </div>
    </Modal>
  );
};

export default LineConnectModal;
